const API = '/api/2026-10/graphql.json';
const PLACEHOLDER =
  'https://cdn.shopify.com/s/files/1/0809/0950/0754/files/kurlective-image-unavailable-placeholder.png?v=1790176568';
const FIELDS = `id handle title vendor availableForSale
  featuredImage { url altText }
  priceRange { minVariantPrice { amount currencyCode } }`;

const storeName = (name) => name.replace(/(?:\s*[-–])?(?:\s+(?:DE|US|UK|GB|EU|AT|CH|DE\/AT)|\s*\((?:DE|US|UK|GB|EU|AT|CH|DE\/AT)\))\s*$/, '').trim() || name;

let storeIds = Promise.resolve([]);

async function storefront(query, variables) {
  const { country, locale } = window.Shopify ?? {};
  const response = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `query Recs($country: CountryCode, $language: LanguageCode, ${query.args})
        @inContext(country: $country, language: $language) { ${query.body} }`,
      variables: { country, language: locale?.toUpperCase().replace('-', '_'), ...variables },
    }),
  });
  const { data, errors } = await response.json();
  if (errors?.length) throw new Error(errors[0].message);
  return data;
}

function recommendations(productId) {
  return storefront(
    { args: '$id: ID!', body: `productRecommendations(productId: $id, intent: RELATED) { ${FIELDS} }` },
    { id: productId }
  ).then((data) => data.productRecommendations ?? []);
}

function search(q) {
  return storefront(
    { args: '$q: String!', body: `products(first: 24, query: $q) { nodes { ${FIELDS} } }` },
    { q }
  ).then((data) => data.products.nodes);
}

// Out-of-stock products only backfill: sort is stable, so each tier keeps its order.
const inStockFirst = (products) => products.sort((a, b) => b.availableForSale - a.availableForSale);

const quote = (value) => `"${value.replace(/["\\]/g, '\\$&')}"`;

// Storefront search can't filter on metafields, so the same-theme row reads the L1 collection filtered to this store.
function inCollection(handle, vendor) {
  return storefront(
    {
      args: '$handle: String!, $vendor: String!',
      body: `collection(handle: $handle) { products(first: 24, filters: [{ productVendor: $vendor }]) { nodes { ${FIELDS} } } }`,
    },
    { handle, vendor }
  ).then((data) => data.collection?.products.nodes ?? []);
}

async function sameStore({ vendor, type, l1Collection }) {
  const queries = [
    type && search(`vendor:${quote(vendor)} AND product_type:${quote(type)}`),
    l1Collection && inCollection(l1Collection, vendor),
  ].filter(Boolean);
  return inStockFirst((await Promise.all(queries)).flat().filter((product) => product.vendor === vendor));
}

// Feeds list each size or colour as its own product, e.g. "Seatpost - 30.9mm" and "Seatpost - 31.6mm".
const baseTitle = (title = '') => {
  const base = title.replace(/ - [^-]+$/, '').trim().toLowerCase();
  return base.split(/\s+/).length >= 3 ? base : title.toLowerCase();
};

// Size variants listed as separate products share a photo, re-uploaded with a _<uuid> suffix; key on the bare file name.
// Generic names ("main.png", "1.jpg") are shared by unrelated products, so short names are not keys.
const imageKey = (url) => {
  const name = (url || '').split('?')[0].split('/').pop().replace(/_[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}(?=\.\w+$)/, '');
  return name.replace(/\.\w+$/, '').length >= 10 ? `img:${name}` : '';
};

// Tokenless Storefront can't read metafields, so one search-section request maps candidate ids to their audience handles.
async function audiencesById(products) {
  const ids = [...new Set(products.map((product) => product.id.split('/').pop()))].slice(0, 50);
  if (!ids.length) return {};
  const q = ids.map((id) => `id:${id}`).join(' OR ');
  const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}search?type=product&q=${encodeURIComponent(q)}&section_id=kurlective-recs-audience`);
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  return JSON.parse(html.querySelector('[data-k-audiences]')?.textContent || '{}');
}

const escape = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// Copy the store's own money format from a sample Liquid rendered, e.g. "£1,234.56" or "€1.234,56".
let moneySample = '';
let outOfStockLabel = '';
function formatMoney(amount, currency) {
  const match = moneySample.match(/^(.*?)1(\D?)234(\D)56(.*)$/);
  if (!match) return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(amount);
  const [, prefix, thousands, decimal, suffix] = match;
  const [whole, cents] = Number(amount).toFixed(2).split('.');
  return `${prefix}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${decimal}${cents}${suffix}`;
}

const sized = (url, width) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`;

function card(product) {
  const { amount, currencyCode } = product.priceRange.minVariantPrice;
  const price = formatMoney(amount, currencyCode);
  const image = product.featuredImage
    ? `<img src="${escape(sized(product.featuredImage.url, 800))}" srcset="${[320, 480, 640, 800].map((w) => `${escape(sized(product.featuredImage.url, w))} ${w}w`).join(', ')}" sizes="(max-width: 749px) 50vw, 25vw" alt="${escape(product.featuredImage.altText || product.title)}" loading="lazy">`
    : `<img class="k-pdp-continuity-card__placeholder" src="${PLACEHOLDER}" alt="" width="1100" height="1375" loading="lazy" aria-hidden="true">`;
  return `<a class="product-card k-pdp-continuity-card" href="${window.Shopify?.routes?.root ?? '/'}products/${escape(product.handle)}">
    <div class="image">${image}</div>
    <div class="body">
      <div class="brand">${escape(storeName(product.vendor))}</div>
      <div class="name">${escape(product.title)}</div>
      <div class="bottom"><span class="price">${escape(price)}</span>${
        product.availableForSale
          ? ''
          : `<span class="k-pdp-continuity-card__stock" style="margin-left:10px;color:var(--k-pdp-muted);font-family:var(--k-pdp-ui);font-size:12px">${escape(outOfStockLabel)}</span>`
      }</div>
    </div>
  </a>`;
}

class PdpRecs extends HTMLElement {
  connectedCallback() {
    moneySample ||= this.dataset.moneySample ?? '';
    outOfStockLabel ||= this.dataset.outOfStockLabel ?? '';
    const loaded = this.#load().catch((error) => {
      console.error('PDP recommendations error:', error.message);
      return [];
    });
    if (this.dataset.mode === 'store') storeIds = loaded;
  }

  async #load() {
    const { mode, productId, limit, title, type, image } = this.dataset;
    const audiences = (this.dataset.audiences ?? '').split(' ').filter(Boolean);
    const seen = new Set([`gid://shopify/Product/${productId}`, baseTitle(title), imageKey(image)]);
    let products;
    if (mode === 'store') {
      products = await sameStore(this.dataset);
    } else {
      // Shopify's recommendations are in stock only; same-type products backfill the rest.
      const [recommended, sameType] = await Promise.all([
        recommendations(`gid://shopify/Product/${productId}`).catch(() => []),
        type ? search(`product_type:${quote(type)}`).catch(() => []) : [],
      ]);
      // Read after the fetches so a store row later in the page has registered its promise.
      (await storeIds).forEach((key) => seen.add(key));
      products = [...recommended, ...inStockFirst(sameType)];
    }

    const audienceMap = audiences.length ? await audiencesById(products).catch(() => ({})) : {};
    const fitsAudience = (product) => {
      const theirs = audienceMap[product.id.split('/').pop()] ?? [];
      return !theirs.length || theirs.some((audience) => audiences.includes(audience));
    };

    const max = Number(limit) || 4;
    const picked = [];
    for (const product of products) {
      if (picked.length >= max) break;
      const base = baseTitle(product.title);
      const img = imageKey(product.featuredImage?.url);
      if (seen.has(product.id) || seen.has(base) || (img && seen.has(img)) || !fitsAudience(product)) continue;
      seen.add(product.id).add(base).add(img);
      picked.push(product);
    }
    // The store row needs two cards to read as a row; related shows whatever is relevant.
    if (picked.length < (mode === 'store' ? 2 : 1)) return [];

    this.querySelector('.k-pdp-continuity__grid').innerHTML = picked.map(card).join('');
    this.querySelector('.k-pdp-continuity').hidden = false;
    return picked.flatMap((product) => [product.id, baseTitle(product.title), imageKey(product.featuredImage?.url)]);
  }
}

if (!customElements.get('k-pdp-recs')) customElements.define('k-pdp-recs', PdpRecs);
